/**
 * nts-hl-drawer.js — v3
 * CartDrawer — reactive diff-rendering drawer for GQL cart.
 * Pure UI. No API calls. Subscribes to bus events.
 *
 * Uses lineId (GQL line ID) instead of key (legacy).
 */
class CartDrawer {
  constructor({ bus, cache, cart, el, config }) {
    this._bus    = bus;
    this._cache  = cache;
    this._cart   = cart;
    this._el     = el;
    this._config = config;
    this._open   = false;
    this._rendered = new Map();

    bus.on('cart:updated',    (e) => this._onUpdated(e));
    bus.on('cart:cleared',    ()  => this._onCleared());
    bus.on('cart:api:start',  (e) => this._onApiStart(e));
    bus.on('cart:api:idle',   ()  => this._onApiIdle());
    bus.on('cart:api:error',  (e) => this._onApiError(e));
    bus.on('cart:batch:empty',(e) => this._onBatchEmpty(e));
    bus.on('cart:api:success',(e) => this._onApiSuccess(e));

    if (this._el) {
      this._el.addEventListener('click', (e) => { if (e.target === this._el) this.close(); });
      this._el.addEventListener('close', () => { this._open = false; this._bus.emit('cart:drawer:closed'); });

      // Coupon form + chip remove — delegated so re-rendering the footer
      // inner HTML doesn't break the bindings.
      const footer = this._el.querySelector('[data-drawer-footer]');
      if (footer) {
        footer.addEventListener('submit', (e) => this._onCouponSubmit(e));
        footer.addEventListener('click',  (e) => this._onFooterClick(e));
      }
    }
  }

  open() {
    if (!this._el) return;
    this._open = true;
    this._el.showModal();
    this._bus.emit('cart:drawer:opened');
    this.render(this._cache.getCart());
  }

  close() {
    if (!this._el) return;
    this._open = false;
    this._el.close();
    this._bus.emit('cart:drawer:closed');
  }

  toggle() { this._open ? this.close() : this.open(); }

  _onUpdated({ cart }) {
    if (this._open) this._diffRender(cart);
    this._updateBadge(cart);
    this._updateSI(cart);
  }

  _onCleared() {
    this._renderEmpty();
    this._updateBadge({ items: [] });
    this._updateSI({ items: [] });
  }

  _onApiStart() {
    // No loading overlay/dimming: every op now updates the cache optimistically,
    // so the UI already reflects the change. The server round-trip is silent
    // background reconciliation — showing a spinner would only make it feel slow.
  }

  _onApiIdle() { this._setDrawerLoading(false); }

  _onApiError({ error }) {
    this._showToast('Cart error: ' + error, 'error');
    this._setDrawerLoading(false);
    this._el?.querySelectorAll('.nts-lc-line--loading').forEach(el => {
      el.classList.remove('nts-lc-line--loading');
      el.querySelectorAll('button').forEach(b => b.disabled = false);
    });
    // An optimistic change was shown but the server rejected it — re-sync to
    // the authoritative cart so the UI self-heals instead of lying.
    if (this._cart && typeof this._cart.sync === 'function') this._cart.sync();
  }

  _onBatchEmpty() { this._showToast('Items already in cart', 'info'); }

  _onApiSuccess({ trigger }) {
    const policy = this._config.drawer?.autoOpen || {};
    const shouldOpen = (
      (trigger === 'bundle' && policy.onBundle !== false) ||
      (trigger === 'upsell' && policy.onUpsell) ||
      (trigger === 'single' && policy.onAdd !== false)
    );
    // Don't auto-open if the add left nothing visible — e.g. the shopper deleted
    // the item (pending-removal) and closed the drawer while the add was still
    // in flight. Without this, the late add response pops the drawer back open
    // for an item that's already gone.
    const hasVisible = this._displayItems(this._cache.getCart()).length > 0;
    if (shouldOpen && hasVisible && !this._open) this.open();
  }

  /* ── Rendering ── */

  // Ship-Insure renders as a toggle row in the footer (below the coupon
  // input), not as a cart line — filter it out of the line list. Free gift(s)
  // are pinned to the TOP so the order is stable (gift first, product second)
  // and doesn't visibly reshuffle when the server's line order differs from
  // the optimistic one.
  _displayItems(cart) {
    // Hide SI (rendered as a footer toggle) and any variant with a delete in
    // flight (instant-delete: gone on click, stays gone until the server remove
    // lands — so an unsettled add can't flash it back).
    const pending = this._cart && typeof this._cart.isPendingRemove === 'function'
      ? (id) => this._cart.isPendingRemove(id) : () => false;
    const items = (cart.items || []).filter(i => !this._isSI(i) && !pending(i.variantId));
    return items.slice().sort((a, b) =>
      (this._isFG(b) ? 1 : 0) - (this._isFG(a) ? 1 : 0)
    );
  }

  render(cart) {
    const body = this._el?.querySelector('[data-drawer-body]');
    if (!body) return;
    const items = this._displayItems(cart);
    // Empty cart → just the message. _renderEmpty() also clears the footer, so
    // no coupon input / SI toggle / checkout button lingers over an empty cart.
    if (!items.length) { this._renderEmpty(); return; }
    body.innerHTML = '';
    this._rendered.clear();
    items.forEach(item => this._appendLine(body, item));
    this._renderFooter(cart);
  }

  _diffRender(cart) {
    const body = this._el?.querySelector('[data-drawer-body]');
    if (!body) return;
    const dItems = this._displayItems(cart);
    if (!dItems.length) { this._renderEmpty(); return; }
    // NOTE: lines render from dItems (SI filtered out), but the footer must
    // receive the FULL cart — it needs the SI item for the pill.

    // Transitioning from empty → non-empty: _rendered is empty but the body
    // still holds the static "Your cart is empty." placeholder (or stale DOM).
    // Drop straight to a full render so we don't end up with leftover content
    // sitting above the freshly-appended lines.
    if (this._rendered.size === 0) { this.render(cart); return; }

    const newIds = new Set(dItems.map(i => i.lineId));
    const currentIds = new Set(this._rendered.keys());

    currentIds.forEach(id => {
      if (!newIds.has(id)) {
        const el = body.querySelector('[data-line-id="' + id + '"]');
        const prev = this._rendered.get(id);
        if (el) {
          if (prev && prev.optimistic) {
            // Optimistic ghost being replaced by the real GQL line — yank
            // instantly so the real line slides into the same visual slot
            // without a fade-out flicker.
            el.remove();
          } else {
            el.classList.add('nts-lc-line--removing');
            setTimeout(() => el.remove(), 200);
          }
        }
        this._rendered.delete(id);
      }
    });

    dItems.forEach(item => {
      if (!currentIds.has(item.lineId)) this._appendLine(body, item);
      else this._updateLine(item);
    });

    // Keep DOM order in sync with dItems (free gift pinned to top). _appendLine
    // always appends at the end, so a gift that arrives AFTER its product would
    // otherwise sit BELOW it — reorder so the sorted list wins.
    dItems.forEach((item, idx) => {
      const el = body.querySelector('[data-line-id="' + item.lineId + '"]');
      if (el && body.children[idx] !== el) body.insertBefore(el, body.children[idx] || null);
    });

    this._renderFooter(cart);
  }

  _appendLine(body, item) {
    const isSI = this._isSI(item);
    // A line marked with `_gift` attribute is a *candidate* free gift. It only
    // renders as FREE if it's optimistic (pre-GQL — we trust we'll discount it)
    // or the server confirms the line resolved to $0. Otherwise show the line
    // at its real (non-discounted) price so the user isn't told something is
    // FREE when it actually isn't.
    const isGiftAttr = this._isFG(item);
    const giftFree = isGiftAttr && (item.optimistic || item.lineTotal === 0);
    const isFG = giftFree;
    // A gift-attributed line is shopper-uncontrollable regardless of whether
    // the $0 discount has resolved yet — it never gets a qty stepper or trash.
    const isGiftChrome = isGiftAttr;
    const cls = isSI ? ' nts-lc-line--si' : isGiftAttr ? ' nts-lc-line--fg' : '';

    const div = document.createElement('div');
    div.className = 'nts-lc-line' + cls;
    div.dataset.lineId = item.lineId;
    div.dataset.variantId = item.variantId;

    const imgSrc = item.image ? item.image + (item.image.indexOf('?') >= 0 ? '&' : '?') + 'width=220' : '';
    const imgHtml = imgSrc
      ? '<img src="' + this._esc(imgSrc) + '" alt="" loading="lazy" />'
      : '<div class="nts-lc-line__no-img"></div>';

    const variantHtml = item.variantTitle
      ? '<div class="nts-lc-line__variant">' + this._esc(item.variantTitle) + '</div>'
      : '';

    // Price block — show the LINE total (unit × qty), so increasing quantity
    // updates the displayed amount. Prefer the server line cost when present.
    //   free gift  → strike the variant's regular price + FREE label
    //   sale       → strike compareAtPrice (× qty) + show line total
    //   regular    → line total
    const qty = item.quantity || 1;
    const lineAmount = item.lineTotal != null ? item.lineTotal : (item.price || 0) * qty;
    const compareAmount = item.comparePrice != null ? item.comparePrice * qty : 0;
    let priceHtml;
    if (isFG) {
      // Strike the gift's REGULAR price. Optimistically that's comparePrice;
      // after server sync the gift has no compareAtPrice but its variant price
      // IS the regular price — fall back to it so the struck amount stays
      // consistent (no flicker where the strikethrough vanishes).
      const giftRegular = (item.comparePrice != null ? item.comparePrice : (item.price || 0)) * qty;
      const strikeHtml = giftRegular > 0
        ? '<span class="nts-lc-line__compare">$' + Math.round(giftRegular / 100) + '</span>'
        : '';
      priceHtml = strikeHtml + '<span class="nts-lc-line__free">FREE</span>';
    } else if (compareAmount > lineAmount) {
      priceHtml =
        '<span class="nts-lc-line__compare">$' + (compareAmount / 100).toFixed(0) + '</span>' +
        '<span data-line-price>$' + (lineAmount / 100).toFixed(2).replace(/\.00$/, '') + '</span>';
    } else {
      priceHtml = '<span data-line-price>$' + (lineAmount / 100).toFixed(2).replace(/\.00$/, '') + '</span>';
    }

    const trashSvg =
      '<svg width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden="true">' +
        '<path d="M2 5h14M7 5V3a1 1 0 011-1h2a1 1 0 011 1v2M4 5l1 13a2 2 0 002 2h4a2 2 0 002-2l1-13" ' +
              'stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    // Free gift is auto-managed: no quantity stepper, no trash button. It's
    // added/removed by the coupon logic, not directly by the shopper.
    const qtyHtml = isGiftChrome
      ? ''
      : '<div class="nts-lc-line__qty">' +
          '<button class="nts-lc-qty-btn" data-action="decrease" data-lid="' + this._esc(item.lineId) + '" data-qty="' + item.quantity + '" aria-label="Decrease quantity">−</button>' +
          '<span data-line-qty>' + item.quantity + '</span>' +
          '<button class="nts-lc-qty-btn" data-action="increase" data-lid="' + this._esc(item.lineId) + '" data-qty="' + item.quantity + '" aria-label="Increase quantity">+</button>' +
        '</div>';
    const removeHtml = isGiftChrome
      ? ''
      : '<button class="nts-lc-line__remove" data-action="remove" data-lid="' + this._esc(item.lineId) + '" aria-label="Remove item">' + trashSvg + '</button>';

    // Production layout: image left; content column fills the image height with
    // title/variant + remove at the TOP and qty (left) + price (right) pinned to
    // the BOTTOM footer of the line.
    div.innerHTML =
      '<div class="nts-lc-line__img">' + imgHtml + '</div>' +
      '<div class="nts-lc-line__col">' +
        '<div class="nts-lc-line__top">' +
          '<div class="nts-lc-line__info">' +
            '<div class="nts-lc-line__title">' + this._esc(item.title) + '</div>' +
            variantHtml +
          '</div>' +
          removeHtml +
        '</div>' +
        '<div class="nts-lc-line__bottom">' +
          qtyHtml +
          '<div class="nts-lc-line__price">' + priceHtml + '</div>' +
        '</div>' +
      '</div>' +
      '<div class="nts-lc-line__spinner"></div>';

    div.addEventListener('click', (e) => this._onLineClick(e));
    body.appendChild(div);
    this._rendered.set(item.lineId, { ...item });
  }

  _updateLine(item) {
    const el = this._el?.querySelector('[data-line-id="' + item.lineId + '"]');
    if (!el) return;
    const prev = this._rendered.get(item.lineId);
    if (!prev) return;

    if (prev.quantity !== item.quantity) {
      const qtyEl = el.querySelector('[data-line-qty]');
      if (qtyEl) qtyEl.textContent = item.quantity;
      el.querySelectorAll('[data-qty]').forEach(b => b.dataset.qty = item.quantity);
    }
    if (prev.price !== item.price || prev.quantity !== item.quantity || prev.lineTotal !== item.lineTotal) {
      const priceEl = el.querySelector('[data-line-price]');
      if (priceEl) {
        const qty = item.quantity || 1;
        const lineAmount = item.lineTotal != null ? item.lineTotal : (item.price || 0) * qty;
        priceEl.textContent = '$' + (lineAmount / 100).toFixed(2).replace(/\.00$/, '');
      }
    }
    el.classList.remove('nts-lc-line--loading');
    el.querySelectorAll('button').forEach(b => b.disabled = false);
    this._rendered.set(item.lineId, { ...item });
  }

  _renderEmpty() {
    const body = this._el?.querySelector('[data-drawer-body]');
    if (body) body.innerHTML = '<div class="nts-lc-drawer__empty">Your cart is currently empty.</div>';
    this._rendered.clear();
    const footer = this._el?.querySelector('[data-drawer-footer]');
    if (footer) footer.innerHTML = '';
  }

  _renderFooter(cart) {
    const footer = this._el?.querySelector('[data-drawer-footer]');
    if (!footer) return;
    const checkoutUrl = cart.checkoutUrl || '/checkout';

    const chipX =
      '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">' +
        '<path d="M1 1 L9 9 M9 1 L1 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
      '</svg>';

    // Ship-Insure activate/deactivate toggle pill — ALWAYS shown directly under
    // the coupon input. Active = SI in cart. Click toggles add/remove (and the
    // session opt-out flag), so removal is reversible (no one-way latch). Price
    // comes from the real SI cart line when present, else the config placeholder.
    const si = this._config.shipInsure || {};
    const siLine = si.variantId
      ? (cart.items || []).find(i => i.variantId === si.variantId)
      : null;
    const siActive = !!siLine;
    const siCents = siLine && siLine.price != null ? siLine.price : (si.price || 0);
    const siPrice = '$' + (siCents / 100).toFixed(2);
    const siToggleHtml = si.variantId
      ? '<button type="button" class="nts-lc-si-toggle' + (siActive ? ' is-active' : '') + '"' +
          ' data-action="toggle-si" role="switch" aria-checked="' + (siActive ? 'true' : 'false') + '">' +
          '<span class="nts-lc-si-toggle__track"><span class="nts-lc-si-toggle__knob"></span></span>' +
          '<span class="nts-lc-si-toggle__label">Ship-Insure — Worry-Free Order Protection</span>' +
          '<span class="nts-lc-si-toggle__price">' + siPrice + '</span>' +
        '</button>'
      : '';

    const chipsHtml = (cart.discountCodes || []).map(d => {
      const mod = d.applicable ? '' : ' nts-lc-coupon__chip--invalid';
      return '<span class="nts-lc-coupon__chip' + mod + '" data-code="' + this._esc(d.code) + '">' +
        '<span class="nts-lc-coupon__chip-label">' + this._esc(d.code) + '</span>' +
        '<button type="button" class="nts-lc-coupon__chip-x" data-action="remove-coupon" data-code="' + this._esc(d.code) + '" aria-label="Remove ' + this._esc(d.code) + '">' + chipX + '</button>' +
      '</span>';
    }).join('');

    footer.innerHTML =
      '<div class="nts-lc-coupon">' +
        '<p class="nts-lc-coupon__info">Enter codes below to be applied at checkout.</p>' +
        '<form class="nts-lc-coupon__form" data-coupon-form autocomplete="off" novalidate>' +
          '<input type="text" class="nts-lc-coupon__input" placeholder="ENTER COUPON CODES HERE" data-coupon-input aria-label="Coupon code" />' +
          '<button type="submit" class="nts-lc-coupon__apply">APPLY</button>' +
        '</form>' +
        // Applied-code chips sit directly under the input (where the user typed
        // them), BEFORE the Ship-Insure toggle row.
        (chipsHtml ? '<div class="nts-lc-coupon__chips">' + chipsHtml + '</div>' : '') +
        siToggleHtml +
      '</div>' +
      '<a href="' + this._esc(checkoutUrl) + '" class="nts-lc-drawer__checkout">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="14" viewBox="0 0 10 14" fill="none" aria-hidden="true">' +
          '<path d="M10 11.9999V5.93165C10 5.58541 9.71671 5.30212 9.37048 5.30212H0.629525C0.283286 5.30212 0 5.58541 0 5.93165V11.9999C0 13.2212 4.58609 13.1088 5.06453 13.1088C5.54297 13.1088 10 13.2212 10 11.9999Z" fill="white"/>' +
          '<path fill-rule="evenodd" clip-rule="evenodd" d="M8.41691 5.60432V4.42396C8.41691 2.53854 6.94382 0.923805 5.0584 0.889181C3.17297 0.854558 1.57397 2.40004 1.57397 4.31065V5.60432H2.83302V4.31065C2.83302 3.1102 3.84836 2.12622 5.03528 2.14802C6.1818 2.16907 7.15786 3.17096 7.15786 4.42396V5.60432H8.41691Z" fill="white"/>' +
        '</svg>' +
        'CHECKOUT</a>';
  }

  /* ── Coupon handlers (delegated; bound once in constructor) ── */
  async _onCouponSubmit(e) {
    if (!e.target.matches('[data-coupon-form]')) return;
    e.preventDefault();
    const input = e.target.querySelector('[data-coupon-input]');
    const code = (input.value || '').trim().toUpperCase();
    if (!code) return;
    input.value = '';

    // If the typed code drives a free-gift scenario, mirror the ATC path:
    // inject the gift FIRST (when the cart qualifies) so the code applies
    // against a real $0 gift line, then apply the code. Without this, a
    // manually-typed code sets a code with nothing for the Function to discount.
    const bundles = this._config.bundles || {};
    let scenario = null;
    Object.keys(bundles).forEach((k) => {
      const sc = bundles[k];
      if (sc.discountCode && sc.discountCode.toUpperCase() === code) scenario = sc;
    });

    const cart = this._cache.getCart();
    const giftScenario = (scenario && scenario.freeGift) ? scenario : null;

    if (giftScenario) {
      const giftId = giftScenario.freeGift.variantId;
      const qualifies = (cart.items || []).some(i =>
        i.variantId !== giftId &&
        (typeof giftScenario.qualify === 'function' ? giftScenario.qualify(i) : true)
      );
      if (!qualifies) {
        this._showToast('Add an eligible product to unlock the free gift', 'info');
        // Still apply the code so it's on the cart for when they add one.
      }
    }

    // 1) Apply the code FIRST so it's on the cart, then 2) add the gift — the
    // Function then discounts the gift line within the add mutation (same as
    // the add-to-cart path), so it resolves to $0 immediately instead of
    // flashing at full price.
    const existing = (cart.discountCodes || []).map(d => d.code);
    if (!existing.includes(code)) await this._cart.applyDiscount([...existing, code]);

    if (giftScenario) {
      const giftId = giftScenario.freeGift.variantId;
      const after = this._cache.getCart();
      const hasGift = (after.items || []).some(i => i.variantId === giftId);
      const qualifies = (after.items || []).some(i =>
        i.variantId !== giftId &&
        (typeof giftScenario.qualify === 'function' ? giftScenario.qualify(i) : true)
      );
      if (qualifies && !hasGift) {
        const g = giftScenario.freeGift;
        await this._cart.add(
          [{ merchandiseId: 'gid://shopify/ProductVariant/' + giftId, quantity: 1,
             attributes: [{ key: '_gift', value: 'coupon' }] }],
          'coupon',
          { optimisticItems: [{ variantId: giftId, title: g.title, variantTitle: g.variantTitle || null,
             price: 0, comparePrice: g.price, image: g.image, quantity: 1,
             attributes: [{ key: '_gift', value: 'coupon' }] }] }
        );
      }
    }
  }

  _onFooterClick(e) {
    // Checkout: the UI ran ahead of the backend optimistically — before handing
    // off to Shopify's checkout, wait for the queue to drain so the server cart
    // matches what the shopper sees. This is the ONLY place we block on it.
    const checkoutLink = e.target.closest('.nts-lc-drawer__checkout');
    if (checkoutLink && this._cart && typeof this._cart.whenIdle === 'function') {
      const href = checkoutLink.getAttribute('href');
      e.preventDefault();
      checkoutLink.classList.add('nts-lc-drawer__checkout--loading');
      this._cart.whenIdle().then(() => {
        window.location.href = this._cache.getCart().checkoutUrl || href;
      });
      return;
    }

    const siBtn = e.target.closest('[data-action="toggle-si"]');
    if (siBtn) {
      e.preventDefault();
      const si = this._config.shipInsure || {};
      const siRule = this._config.rules?.shipInsure;
      const active = (this._cache.getCart().items || []).some(i => i.variantId === si.variantId);
      if (active) {
        // Deactivate: remove + opt out of auto-injection (reversible).
        if (siRule) siRule.enabled = false;
        try { localStorage.setItem('nts_si_optout', '1'); } catch (err) {}
        this._cart.removeByVariant(si.variantId);
      } else {
        // Activate: clear opt-out, re-enable the rule, add the line now.
        if (siRule) siRule.enabled = true;
        try { localStorage.removeItem('nts_si_optout'); } catch (err) {}
        const line = { merchandiseId: 'gid://shopify/ProductVariant/' + si.variantId, quantity: 1 };
        if (si.attributes) line.attributes = si.attributes;
        this._cart.add([line], 'si');
      }
      return;
    }
    const btn = e.target.closest('[data-action="remove-coupon"]');
    if (!btn) return;
    e.preventDefault();
    // Removing the pill is a deliberate opt-out: strip the code + its gift AND
    // stop it auto-applying on later adds (until a fresh ?token= reload).
    this._cart.optOutCoupon(btn.dataset.code);
  }

  _onLineClick(e) {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    const action = btn.dataset.action;
    const lineId = btn.dataset.lid;
    const qty = parseInt(btn.dataset.qty, 10) || 1;

    if (action === 'remove') this._cart.remove(lineId);
    else if (action === 'decrease') this._cart.updateQty(lineId, qty - 1);
    else if (action === 'increase') this._cart.updateQty(lineId, qty + 1);
  }

  _setLineLoading(lineId, loading) {
    const el = this._el?.querySelector('[data-line-id="' + lineId + '"]');
    if (!el) return;
    el.classList.toggle('nts-lc-line--loading', loading);
    el.querySelectorAll('button').forEach(b => b.disabled = loading);
  }

  _setDrawerLoading(loading) { this._el?.classList.toggle('nts-lc-drawer--loading', loading); }

  _updateBadge(cart) {
    const badge = document.querySelector('[data-cart-badge]');
    if (!badge) return;
    // Count the lines the shopper sees in the drawer (products + free gift; SI
    // is a footer toggle, not a line). The header CSS hides the badge via
    // [data-count="0"], so we MUST keep that attribute in sync — not just the
    // text — or the badge stays permanently hidden.
    const count = this._displayItems(cart).reduce((s, i) => s + (i.quantity || 0), 0);
    badge.textContent = count || '';
    badge.setAttribute('data-count', count || 0);
    badge.classList.toggle('nts-lc-badge--active', count > 0);
  }

  _updateSI(cart) {
    const cb = document.querySelector('[data-si-checkbox]');
    const st = document.querySelector('[data-si-status]');
    if (!cb && !st) return;
    const siId = this._config.rules?.shipInsure?.variantId;
    if (!siId) return;
    const has = (cart.items || []).some(i => i.variantId === siId);
    if (cb) cb.checked = has;
    if (st) { st.textContent = has ? 'Added to order' : 'Not added'; st.classList.toggle('nts-lc-si--active', has); }
  }

  _showToast(message, type) {
    const t = document.createElement('div');
    t.className = 'nts-lc-toast nts-lc-toast--' + (type || 'info');
    t.textContent = message;
    document.body.appendChild(t);
    requestAnimationFrame(() => t.classList.add('nts-lc-toast--visible'));
    setTimeout(() => { t.classList.remove('nts-lc-toast--visible'); setTimeout(() => t.remove(), 300); }, 2500);
  }

  _isSI(item) {
    return (this._config.rules?.shipInsure?.variantId === item.variantId)
        || (item.attributes || []).some(a => a.key === '_si');
  }
  _isFG(item) {
    return (this._config.rules?.freeGift?.variantId === item.variantId)
        || (item.attributes || []).some(a => a.key === '_gift');
  }
  _esc(str) { if (!str) return ''; const d = document.createElement('div'); d.textContent = str; return d.innerHTML; }
}

window.NTS = window.NTS || {};
